import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShoppingBag, Plus, X, Loader2, Check, Ban, FileText, Users, Package,
  Eye, CheckCircle2, AlertCircle, Search, Filter,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDate, formatDateTime } from '@/lib/api'

interface Fornecedor { id: number; codigo: string | null; nome: string; documento: string | null; empresa_parceira_id: number | null }
interface Produto { id: number; codigo: string; descricao: string; preco_custo: number; ultimo_custo: number; is_industrial?: boolean }
interface Deposito { id: number; nome: string; is_default: boolean; ativo: boolean }

interface CompraItem {
  id?: number
  produto_id: number
  produto_codigo?: string
  produto_descricao?: string
  quantidade: number
  preco_unitario: number
  valor_desconto: number
  valor_total: number
}

interface PedidoCompra {
  id: number
  numero: string
  fornecedor_id: number
  fornecedor_nome: string | null
  fornecedor_parceira_empresa_id: number | null
  deposito_id: number | null
  status: string
  data_emissao: string | null
  previsao_entrega: string | null
  condicao_pagamento: string | null
  valor_produtos: number
  valor_frete: number
  valor_total: number
  observacoes: string | null
  itens: CompraItem[]
}

const STATUS_COR: Record<string, string> = {
  rascunho: 'bg-ink-200 text-ink-700',
  aberto: 'bg-sky-100 text-sky-800',
  recebido: 'bg-emerald-100 text-emerald-800',
  cancelado: 'bg-rose-100 text-rose-700',
}

export default function ComprasPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [status, setStatus] = useState<string>('')
  const [showNovo, setShowNovo] = useState(false)
  const [busca, setBusca] = useState('')
  const [fornecedorFiltro, setFornecedorFiltro] = useState<string>('')
  const [previsaoDe, setPrevisaoDe] = useState('')
  const [previsaoAte, setPrevisaoAte] = useState('')
  const [intercompanyFiltro, setIntercompanyFiltro] = useState<string>('') // '', 'sim', 'nao'
  const [valorMin, setValorMin] = useState('')
  const [valorMax, setValorMax] = useState('')

  const { data: pedidos = [], isLoading } = useQuery<PedidoCompra[]>({
    queryKey: ['compras-pedidos', status],
    queryFn: async () => (await api.get('/compras/pedidos', { params: { status: status || undefined } })).data,
  })

  const { data: fornecedoresOpts = [] } = useQuery<Fornecedor[]>({
    queryKey: ['fornecedores-compra'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'fornecedor', limit: 500 } })).data,
  })

  const pedidosFiltrados = useMemo(() => {
    return pedidos.filter(p => {
      if (busca) {
        const t = busca.toLowerCase()
        const m = (p.numero || '').toLowerCase().includes(t)
          || (p.fornecedor_nome || '').toLowerCase().includes(t)
          || String(p.id).includes(t)
        if (!m) return false
      }
      if (fornecedorFiltro && p.fornecedor_id !== Number(fornecedorFiltro)) return false
      if (previsaoDe && (!p.previsao_entrega || p.previsao_entrega < previsaoDe)) return false
      if (previsaoAte && (!p.previsao_entrega || p.previsao_entrega > previsaoAte)) return false
      if (intercompanyFiltro === 'sim' && !p.fornecedor_parceira_empresa_id) return false
      if (intercompanyFiltro === 'nao' && p.fornecedor_parceira_empresa_id) return false
      if (valorMin && Number(p.valor_total) < Number(valorMin)) return false
      if (valorMax && Number(p.valor_total) > Number(valorMax)) return false
      return true
    })
  }, [pedidos, busca, fornecedorFiltro, previsaoDe, previsaoAte, intercompanyFiltro, valorMin, valorMax])

  const totalFiltrado = useMemo(
    () => pedidosFiltrados.reduce((s, p) => s + Number(p.valor_total || 0), 0),
    [pedidosFiltrados]
  )

  const limparFiltros = () => {
    setBusca(''); setFornecedorFiltro(''); setPrevisaoDe(''); setPrevisaoAte('')
    setIntercompanyFiltro(''); setValorMin(''); setValorMax('')
  }
  const filtrosAtivos = (busca ? 1 : 0) + (fornecedorFiltro ? 1 : 0) + (previsaoDe ? 1 : 0)
    + (previsaoAte ? 1 : 0) + (intercompanyFiltro ? 1 : 0) + (valorMin ? 1 : 0) + (valorMax ? 1 : 0)

  const confirmar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/compras/pedidos/${id}/confirmar`)).data,
    onSuccess: () => { toast.success('Pedido confirmado'); qc.invalidateQueries({ queryKey: ['compras-pedidos'] }) },
  })
  const cancelar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/compras/pedidos/${id}/cancelar`)).data,
    onSuccess: () => { toast.success('Pedido cancelado'); qc.invalidateQueries({ queryKey: ['compras-pedidos'] }) },
  })

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingBag className="text-brand-600" /> Compras
          </h1>
          <p className="text-ink-600 text-sm">Pedidos de compra e recebimento de NFs.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav('/compras/nf-entradas')} className="btn-secondary">
            <FileText size={16} /> NFs de Entrada
          </button>
          <button onClick={() => setShowNovo(true)} className="btn-primary">
            <Plus size={16} /> Novo Pedido
          </button>
        </div>
      </div>

      <div className="card card-body space-y-3">
        {/* Linha 1: busca + status */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-9 !py-1.5 text-sm" placeholder="Buscar por nº, fornecedor..."
              value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
          <span className="text-ink-600 text-xs">Status:</span>
          {['', 'rascunho', 'aberto', 'recebido', 'cancelado'].map(s => (
            <button key={s || 'todos'} onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded text-xs ${status === s ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'}`}>
              {s === '' ? 'Todos' : s}
            </button>
          ))}
        </div>
        {/* Linha 2: filtros avançados */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-ink-100">
          <Filter size={12} className="text-ink-400" />
          <select className="input !py-1 !text-xs w-auto min-w-[160px]"
            value={fornecedorFiltro} onChange={e => setFornecedorFiltro(e.target.value)}>
            <option value="">Todos fornecedores</option>
            {fornecedoresOpts.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          <div className="flex items-center gap-1 text-xs text-ink-600">
            Previsão:
            <input type="date" className="input !py-1 !text-xs w-auto" value={previsaoDe} onChange={e => setPrevisaoDe(e.target.value)} />
            <span>até</span>
            <input type="date" className="input !py-1 !text-xs w-auto" value={previsaoAte} onChange={e => setPrevisaoAte(e.target.value)} />
          </div>
          <select className="input !py-1 !text-xs w-auto"
            value={intercompanyFiltro} onChange={e => setIntercompanyFiltro(e.target.value)}>
            <option value="">Todos (intercompany?)</option>
            <option value="sim">Apenas intercompany</option>
            <option value="nao">Sem intercompany</option>
          </select>
          <div className="flex items-center gap-1 text-xs text-ink-600">
            Valor:
            <input type="number" placeholder="min" className="input !py-1 !text-xs w-20"
              value={valorMin} onChange={e => setValorMin(e.target.value)} />
            <span>até</span>
            <input type="number" placeholder="max" className="input !py-1 !text-xs w-24"
              value={valorMax} onChange={e => setValorMax(e.target.value)} />
          </div>
          {filtrosAtivos > 0 && (
            <button onClick={limparFiltros} className="text-xs text-rose-600 hover:underline ml-1">
              <X size={11} className="inline" /> Limpar ({filtrosAtivos})
            </button>
          )}
          <span className="ml-auto text-xs text-ink-500">
            {pedidosFiltrados.length} de {pedidos.length} · <strong className="text-ink-800">{formatMoney(totalFiltrado)}</strong>
          </span>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>#</th><th>Fornecedor</th><th>Previsão</th><th className="text-right">Itens</th>
              <th className="text-right">Total</th><th>Status</th><th className="w-32"></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && pedidosFiltrados.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-ink-500">
                {pedidos.length === 0 ? 'Nenhum pedido.' : 'Nenhum pedido corresponde aos filtros.'}
              </td></tr>
            )}
            {pedidosFiltrados.map(p => (
              <tr key={p.id}>
                <td className="font-mono">{p.numero}</td>
                <td>
                  <div>{p.fornecedor_nome || `#${p.fornecedor_id}`}</div>
                  {p.fornecedor_parceira_empresa_id && (
                    <span className="text-[10px] bg-sky-100 text-sky-800 px-1 rounded">INTERCOMPANY</span>
                  )}
                </td>
                <td className="text-xs">{formatDate(p.previsao_entrega)}</td>
                <td className="text-right">{p.itens.length}</td>
                <td className="text-right font-semibold">{formatMoney(p.valor_total)}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COR[p.status]}`}>{p.status}</span></td>
                <td>
                  <div className="flex gap-1">
                    {p.status === 'rascunho' && (
                      <button onClick={() => confirmar.mutate(p.id)} className="btn-secondary !py-1 !px-2 text-xs text-emerald-700">
                        <Check size={12} /> Confirmar
                      </button>
                    )}
                    {(p.status === 'rascunho' || p.status === 'aberto') && (
                      <button onClick={() => cancelar.mutate(p.id)} className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                        <Ban size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNovo && <NovoPedidoModal onClose={() => setShowNovo(false)} onDone={() => {
        setShowNovo(false); qc.invalidateQueries({ queryKey: ['compras-pedidos'] })
      }} />}
    </div>
  )
}

// ============================================================
// NOVO PEDIDO
// ============================================================

function NovoPedidoModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [fornecedorId, setFornecedorId] = useState<number | ''>('')
  const [depositoId, setDepositoId] = useState<number | ''>('')
  const [previsao, setPrevisao] = useState('')
  const [condicao, setCondicao] = useState('30 dias')
  const [observacoes, setObservacoes] = useState('')
  const [itens, setItens] = useState<Array<{ produto_id: number | ''; quantidade: string; preco_unitario: string; valor_desconto: string }>>([
    { produto_id: '', quantidade: '1', preco_unitario: '0', valor_desconto: '0' },
  ])

  const { data: fornecedores = [] } = useQuery<Fornecedor[]>({
    queryKey: ['pessoas-fornec'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'fornecedor', limit: 500 } })).data,
  })
  const { data: fornecedoresAmbos = [] } = useQuery<Fornecedor[]>({
    queryKey: ['pessoas-ambos'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'ambos', limit: 500 } })).data,
  })
  const fornecedoresAll = useMemo(() => {
    const map = new Map<number, Fornecedor>()
    for (const p of [...fornecedores, ...fornecedoresAmbos]) map.set(p.id, p)
    return Array.from(map.values())
  }, [fornecedores, fornecedoresAmbos])

  const { data: produtos = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-select-compra'],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { limit: 500 } })).data,
  })
  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos-all'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })

  const total = itens.reduce((s, i) =>
    s + (parseFloat(i.quantidade) || 0) * (parseFloat(i.preco_unitario) || 0) - (parseFloat(i.valor_desconto) || 0), 0)

  const adicionar = () => setItens([...itens, { produto_id: '', quantidade: '1', preco_unitario: '0', valor_desconto: '0' }])
  const remover = (idx: number) => setItens(itens.filter((_, i) => i !== idx))
  const alterar = (idx: number, patch: Partial<typeof itens[0]>) =>
    setItens(itens.map((it, i) => i === idx ? { ...it, ...patch } : it))

  const criar = useMutation({
    mutationFn: async () => (await api.post('/compras/pedidos', {
      fornecedor_id: fornecedorId,
      deposito_id: depositoId || null,
      previsao_entrega: previsao || null,
      condicao_pagamento: condicao || null,
      observacoes: observacoes || null,
      itens: itens.map(i => ({
        produto_id: i.produto_id,
        quantidade: parseFloat(i.quantidade),
        preco_unitario: parseFloat(i.preco_unitario),
        valor_desconto: parseFloat(i.valor_desconto || '0'),
      })),
    })).data,
    onSuccess: (d: PedidoCompra) => { toast.success(`Pedido #${d.numero} criado`); onDone() },
  })

  const pronto = !!fornecedorId && itens.length > 0 && itens.every(i => !!i.produto_id && parseFloat(i.quantidade) > 0)

  // Auto-sugere preço ao escolher produto
  const sugerirPreco = (idx: number, produtoId: number) => {
    const p = produtos.find(pp => pp.id === produtoId)
    if (!p) return
    const preco = Number(p.ultimo_custo) > 0 ? Number(p.ultimo_custo) : Number(p.preco_custo)
    alterar(idx, { produto_id: produtoId, preco_unitario: String(preco || 0) })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">Novo Pedido de Compra</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Fornecedor *</div>
              <select className="input w-full" value={fornecedorId}
                onChange={e => setFornecedorId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">— selecione —</option>
                {fornecedoresAll.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.codigo ? `${f.codigo} - ` : ''}{f.nome}
                    {f.empresa_parceira_id ? ' 🏭 (empresa do grupo)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Depósito (destino)</div>
              <select className="input w-full" value={depositoId}
                onChange={e => setDepositoId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">— default da empresa —</option>
                {depositos.filter(d => d.ativo).map(d => (
                  <option key={d.id} value={d.id}>{d.nome}{d.is_default ? ' (default)' : ''}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium mb-1">Previsão de entrega</div>
              <input type="date" className="input w-full" value={previsao} onChange={e => setPrevisao(e.target.value)} />
            </label>
            <label className="block">
              <div className="text-xs font-medium mb-1">Condição de pagamento</div>
              <input className="input w-full" value={condicao} onChange={e => setCondicao(e.target.value)} />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-sm font-semibold">Itens</div>
              <button onClick={adicionar} className="btn-secondary !py-1 !px-2 text-xs"><Plus size={12} /> Item</button>
            </div>
            <div className="space-y-2">
              {itens.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-ink-50">
                  <div className="col-span-6">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Produto</div>
                    <select className="input w-full text-sm" value={it.produto_id}
                      onChange={e => sugerirPreco(idx, Number(e.target.value))}>
                      <option value="">—</option>
                      {produtos.map(p => <option key={p.id} value={p.id}>{p.codigo} - {p.descricao}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Qtd</div>
                    <input type="number" step="0.001" className="input w-full text-sm"
                      value={it.quantidade} onChange={e => alterar(idx, { quantidade: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">R$ unit.</div>
                    <input type="number" step="0.0001" className="input w-full text-sm"
                      value={it.preco_unitario} onChange={e => alterar(idx, { preco_unitario: e.target.value })} />
                  </div>
                  <div className="col-span-1">
                    <div className="text-[10px] uppercase text-ink-500 mb-0.5">Desc.</div>
                    <input type="number" step="0.01" className="input w-full text-sm"
                      value={it.valor_desconto} onChange={e => alterar(idx, { valor_desconto: e.target.value })} />
                  </div>
                  <div className="col-span-1 text-right">
                    {itens.length > 1 && (
                      <button onClick={() => remover(idx)} className="text-rose-600 p-1"><X size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span className="text-ink-600">Total estimado:</span>
              <strong>{formatMoney(total)}</strong>
            </div>
          </div>

          <label className="block">
            <div className="text-xs font-medium mb-1">Observações</div>
            <textarea className="input w-full" rows={2} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
          </label>
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={!pronto || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
            {criar.isPending && <Loader2 size={14} className="animate-spin" />}
            Criar pedido
          </button>
        </div>
      </div>
    </div>
  )
}
